import React from "react";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";

import AppBar from "@material-ui/core/AppBar";
import CssBaseline from "@material-ui/core/CssBaseline";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import Hidden from "@material-ui/core/Hidden";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import MenuIcon from "@material-ui/icons/Menu";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  makeStyles,
  useTheme,
  Theme,
  createStyles
} from "@material-ui/core/styles";

import MainPanel from "../mainPanel/MainPanel";
import SidePanel from "../sidePanel/SidePanel";
import { RootState } from "../../store";
import { getRecords } from "../../store/records/actions";
import { DRAWER_WIDTH, DRAWER_WIDTH_MOBILE } from "../../constants";
import "./App.scss";

import logo from "../../assets/logo-white.png";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex"
    },
    overrides: {
      MuiToolbar: {
        regular: {
          height: "50px",
          "@media(min-width:600px)": {
            height: "50px"
          }
        }
      }
    },
    drawer: {
      flexShrink: 0,
      [theme.breakpoints.down("sm")]: {
        width: 0
      },
      [theme.breakpoints.up("md")]: {
        width: DRAWER_WIDTH
      }
    },
    appBar: {
      backgroundColor: theme.palette.background.default,
      color: "white",
      [theme.breakpoints.up("sm")]: {
        width: `calc(100% - ${DRAWER_WIDTH_MOBILE}px)`,
        marginRight: DRAWER_WIDTH_MOBILE
      }
    },
    menuButton: {
      marginRight: theme.spacing(2),
      [theme.breakpoints.up("sm")]: {
        display: "none"
      }
    },
    // necessary for content to be below app bar
    // toolbar: theme.mixins.toolbar,
    toolbar: {
      height: "50px",
      minHeight: "50px",
      display: "flex",
      justifyContent: "space-between"
    },
    drawerPaper: {
      [theme.breakpoints.down("sm")]: {
        width: DRAWER_WIDTH_MOBILE
      },
      [theme.breakpoints.up("md")]: {
        width: DRAWER_WIDTH
      }
    },
    content: {
      flexGrow: 1
    }
  })
);

export default function App() {
  const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>();

  const { records, loading, error } = useSelector((state: RootState) => ({
    records: state.records.records.records,
    loading: state.records.records.loading,
    error: state.records.records.error
  }));

  useEffect(() => {
    dispatch(getRecords());
  }, [dispatch]);

  const classes = useStyles();
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  if (error) {
    return <div style={{ color: "white" }}>Error! {error.message}</div>;
  }
  if (loading) {
    return (
      <div id="loading">
        <CircularProgress color={"secondary"} size={80} />
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div id="app" className={classes.root}>
      <CssBaseline />
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <div className="logo-wrapper">
            <img src={logo} className="logo"></img>
            <Typography variant="h6" noWrap>
              Chasing Highpoints
            </Typography>
          </div>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            className={classes.menuButton}
            style={{ marginRight: 0, paddingRight: 0 }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.toolbar} />
        <MainPanel records={records} />
      </main>
      <nav className={classes.drawer} aria-label="mailbox folders">
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        {/* @ts-ignore */}
        <Hidden smUp implementation="css">
          <Drawer
            container={window.document.body}
            variant="temporary"
            anchor="right"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaper
            }}
            ModalProps={{
              keepMounted: false // Better open performance on mobile.
            }}
          >
            <SidePanel records={records} />
          </Drawer>
        </Hidden>
        {/* @ts-ignore */}
        <Hidden xsDown implementation="css">
          <Drawer
            classes={{
              paper: classes.drawerPaper
            }}
            variant="permanent"
            open
            anchor="right"
          >
            <SidePanel records={records} />
          </Drawer>
        </Hidden>
      </nav>
    </div>
  );
}
